


























import Vue, { PropType } from 'vue';
import VueApexCharts from 'vue-apexcharts';
import ResizeObserver from 'resize-observer-polyfill';
import { ApexOptions } from 'apexcharts';
import { BoxplotY } from '@/interfaces/types/ChartTypes';

export default Vue.extend({
  components: {
    apexchart: VueApexCharts,
  },
  props: {
    boxplotData: {
      type: Object as PropType<BoxplotY>,
      default: () => {
        return {
          outlierMin: 0,
          min: 0,
          lowerQt: 1,
          median: 2,
          upperQt: 3,
          max: 5,
          outlierMax: 10,
        };
      },
    },
    minValue: {
      type: Number,
    },
    avgValue: {
      type: Number,
    },
    maxValue: {
      type: Number,
    },
    colors: {
      type: Array as PropType<string[]>,
    },
    showBoxplot: {
      type: Boolean,
      default: false,
    },
    showMinAvgMax: {
      type: Boolean,
      default: true,
    },
  },
  mounted() {
    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(this.$refs.chartContainer as HTMLDivElement);
  },
  beforeDestroy() {
    this.resizeObserver?.disconnect();
  },
  data(): {
    chartWidth: number;
    chartHeight: number;
    resizeObserver: ResizeObserver | null;
  } {
    return {
      chartWidth: 100,
      chartHeight: 100,
      resizeObserver: null,
    };
  },
  computed: {
    /** 최종 계산된 차트 너비 (appending 'px' & div splitting) */
    computedChartWidth(): string {
      return this.showBoxplot && this.showMinAvgMax
        ? this.chartWidth / 2 + 'px'
        : this.chartWidth + 'px';
    },
    /** 최종 계산된 차트 높이 */
    computedChartHeight(): string {
      return this.chartHeight - 30 + 'px';
    },
    /** Boxplot outlier data */
    boxplotOutliers(): {
      name: string;
      type: string;
      data: { x: number; y: number }[];
    } {
      const outliers: { x: number; y: number }[] = [];
      if (
        this.boxplotData.outlierMin &&
        this.boxplotData.outlierMin < this.boxplotData.min
      ) {
        outliers.push({ x: 0, y: this.boxplotData.outlierMin });
      }
      if (
        this.boxplotData.outlierMax &&
        this.boxplotData.outlierMax > this.boxplotData.max
      ) {
        outliers.push({ x: 0, y: this.boxplotData.outlierMax });
      }
      return {
        name: 'outliers',
        type: 'scatter',
        data: outliers,
      };
    },
    /** prop을 통해 계산한 Boxplot 데이터 */
    computedBoxplotSeries(): {
      name: string;
      type: string;
      data: { x: number; y: number | number[] }[];
    }[] {
      const boxplotY = this.boxplotData
        ? [
            this.boxplotData.min,
            this.boxplotData.lowerQt,
            this.boxplotData.median,
            this.boxplotData.upperQt,
            this.boxplotData.max,
          ]
        : [];
      return [
        {
          name: 'box',
          type: 'boxPlot',
          data: [
            {
              x: 0,
              y: boxplotY,
            },
          ],
        },
        this.boxplotOutliers,
      ];
    },
    /** prop을 통해 계산한 최소/평균/최대 차트 데이터 */
    computedMinAvgMaxSeries(): { name: string; data: number[] }[] {
      return [
        {
          name: '최소',
          data: [0, this.minValue ? +this.minValue : 0, 0],
        },
        { name: '평균', data: [0, this.avgValue ? +this.avgValue : 0, 0] },
        {
          name: '최대',
          data: [0, this.maxValue ? +this.maxValue : 0, 0],
        },
      ];
    },
    boxplotOptions(): ApexOptions {
      return {
        dataLabels: {
          enabled: false,
        },
        colors: ['#5BD99C'],
        xaxis: {
          position: 'bottom',
          labels: {
            show: false,
          },
        },
        yaxis: {
          labels: {
            show: true,
          },
        },
        fill: {
          opacity: 1,
        },
        legend: {
          position: 'right',
          fontSize: '12px',
          show: true,
          inverseOrder: true,
        },
        stroke: {
          show: true,
          colors: ['#323C47'],
          width: 0.5,
        },
        plotOptions: {
          boxPlot: {
            colors: {
              upper: '#5BD99C',
              lower: '#FF974D',
            },
          },
        },
      };
    },
    minAvgMaxOptions(): ApexOptions {
      return {
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '200%',
          },
        },
        xaxis: {
          labels: {
            show: false,
          },
          categories: ['', '개수', ''],
        },
        dataLabels: {
          formatter: function (val) {
            return val === 0 ? '' : val.toString();
          },
        },
        tooltip: {
          shared: false,
          intersect: true,
        },
      };
    },
  },
  methods: {
    onResize() {
      const divElement = this.$refs.chartContainer as HTMLDivElement;
      if (divElement) {
        this.chartWidth = Math.min(
          divElement.clientHeight * 1.6,
          divElement.clientWidth,
        );
        this.chartHeight = Math.min(divElement.clientHeight, this.chartWidth);
      }
    },
  },
});
